import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { ApiError } from '../types';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const API_TIMEOUT = 30000; // 30 seconds
export const MAX_RATE_LIMIT_RETRY_AFTER_SECONDS = 60;
export const SESSION_EXPIRED_EVENT = 'aiona:session-expired';

/** Opts a request out of the interceptor's one-shot Retry-After wait (#1257). */
export interface ApiRequestConfig extends AxiosRequestConfig {
  skipRateLimitRetry?: boolean;
}

const dispatchSessionExpiredEvent = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const event = new CustomEvent(SESSION_EXPIRED_EVENT, {
    cancelable: true,
  });
  return !window.dispatchEvent(event);
};

// Create axios instance
// Cookie認証は廃止し、Authorization header のみを使用
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,  // Cookie送信は無効化（Authorization header認証のみ）
});

// Token management - using sessionStorage
// Tokens are stored in sessionStorage and sent via Authorization header
export const setTokens = (
  accessToken: string,
  refreshToken: string,
  expiresIn: number = 3600,
  rotating: boolean = false,
): void => {
  sessionStorage.setItem('access_token', accessToken);
  sessionStorage.setItem('refresh_token', refreshToken);
  sessionStorage.setItem('token_expiry', String(Date.now() + expiresIn * 1000));
  sessionStorage.setItem('refresh_request_id', uuidv4());
  if (!rotating || !sessionStorage.getItem('auth_session_id')) {
    sessionStorage.setItem('auth_session_id', uuidv4());
  }
};

export const clearTokens = (): void => {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token_expiry');
  sessionStorage.removeItem('refresh_request_id');
  sessionStorage.removeItem('auth_session_id');
};

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

let refreshFlight: { token: string; promise: Promise<RefreshResponse> } | undefined;

// Proactive renewal and concurrent 401s share one rotation. Keep the request ID
// across network/503 retries, including a reload or a cloned sessionStorage tab.
export const refreshSessionTokens = (): Promise<RefreshResponse> => {
  const token = sessionStorage.getItem('refresh_token');
  if (!token) return Promise.reject({ code: 'HTTP_401', details: { http_status: 401 } });
  if (refreshFlight?.token === token) return refreshFlight.promise;
  let requestId = sessionStorage.getItem('refresh_request_id');
  if (!requestId) {
    requestId = uuidv4();
    sessionStorage.setItem('refresh_request_id', requestId);
  }
  const flight = {
    token,
    promise: api.post<RefreshResponse>('/auth/refresh', {
      refresh_token: token,
      refresh_request_id: requestId,
    }).then((data) => {
      // Logout/account switching while a request is in flight must win.
      if (sessionStorage.getItem('refresh_token') !== token) {
        return Promise.reject({ code: 'AUTH_SESSION_CHANGED' });
      }
      if (typeof data?.access_token !== 'string' || !data.access_token.trim()
        || typeof data?.refresh_token !== 'string' || !data.refresh_token.trim()) {
        throw Object.assign(new Error('Invalid refresh response'), {
          code: 'INVALID_REFRESH_RESPONSE', details: { reason: 'missing_tokens' },
        });
      }
      setTokens(data.access_token, data.refresh_token, data.expires_in, true);
      return data;
    }).finally(() => {
      if (refreshFlight === flight) refreshFlight = undefined;
    }),
  };
  refreshFlight = flight;
  return flight.promise;
};

// Request interceptor
// Adds Authorization header from sessionStorage
apiClient.interceptors.request.use(
  (config) => {
    const sessionId = sessionStorage.getItem('auth_session_id');
    const tracked = config as typeof config & { _authSessionId?: string | null };
    if (tracked._authSessionId !== undefined && tracked._authSessionId !== sessionId) {
      return Promise.reject({ code: 'AUTH_SESSION_CHANGED' });
    }
    tracked._authSessionId = sessionId;
    // Get access token from sessionStorage and add to Authorization header
    const token = sessionStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // CSRF token は不要（Cookie認証廃止により）

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const nonEmptyString = (value: unknown): string | undefined =>
  (typeof value === 'string' && value ? value : undefined);

// Prose the server actually wrote. `detail` is text only when the route made it
// text: a 422 validation body carries an array and HTTPException(detail={...})
// an object, neither of which may reach `message` — it is declared `string`.
// Such a body carries no prose, so it yields '' rather than falling through to
// axios's English "Request failed with status code N"; callers render their own
// localized copy for an empty message, which is what they did before.
const serverProse = (body?: { message?: unknown; detail?: unknown }): string | undefined =>
  nonEmptyString(body?.message)
  ?? nonEmptyString(body?.detail)
  ?? (body?.detail != null && typeof body.detail !== 'string' ? '' : undefined);

// Transport-layer normalization: every rejection leaves this interceptor with
// the same ApiError shape. Wire codes are the contract; user-facing text is the
// presentation layer's job (see utils/apiErrorMessages.ts).
const normalizeApiError = (
  error: AxiosError<ApiError>,
  overrides: Partial<ApiError> = {},
): ApiError => {
  const status = error.response?.status;
  const body = error.response?.data as (ApiError & { detail?: unknown }) | undefined;
  return {
    message: serverProse(body) ?? nonEmptyString(error.message) ?? '',
    code: nonEmptyString(overrides.code)
      ?? nonEmptyString(body?.code)
      ?? (status ? `HTTP_${status}` : error.code),
    details: {
      ...(body?.details || {}),
      // An object `detail` names which business rule refused; `serverProse`
      // drops it as prose, so it is carried here instead of being lost.
      ...(body?.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)
        ? { detail: body.detail }
        : {}),
      ...(status ? { http_status: status } : {}),
      ...(overrides.details || {}),
    },
  };
};

export const isAuthenticationRejection = (error: unknown): boolean => {
  const failure = error as ApiError | undefined;
  const status = failure?.details?.http_status;
  return status === 401 || status === 403
    || failure?.code === 'HTTP_401' || failure?.code === 'HTTP_403';
};

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as ApiRequestConfig & {
      _retry?: boolean;
      _rateLimitRetry?: boolean;
      _authSessionId?: string | null;
    };

    // Handle 403 Forbidden - don't retry, permission denied.
    // Log the request that was refused, never the response body: authorization
    // failures carry tenant ids, role/permission names and resource identifiers,
    // and the browser console is readable by anything running in the page and is
    // routinely pasted into bug reports. The code is what callers act on anyway.
    if (error.response?.status === 403) {
      console.error(
        '403 Forbidden:',
        `${(error.config?.method || 'get').toUpperCase()} ${error.config?.url ?? ''}`,
      );
    }

    // Handle 429 Too Many Requests separately — must not be confused with
    // an auth failure. If a burst of requests trips the per-user rate
    // limit, the next /auth/refresh attempt would itself return 429; the
    // generic 401 branch below would then treat that as a failed refresh
    // and force-logout the user (observed 2026-05-14 demo flow when
    // opening DocumentDetailPage's metadata tab fired N parallel image
    // blob fetches). This branch never touches session tokens: it waits out
    // Retry-After once on an idempotent request unless the caller set
    // skipRateLimitRetry, then hands the retry hint to the caller.
    if (error.response?.status === 429) {
      // The backend rate-limiter adds `retry_after` to the body; everything
      // else in it is the normalizer's business.
      const rateLimitBody = error.response.data as { retry_after?: number } | undefined;
      const retryAfterHeader = error.response.headers?.['retry-after'];
      const normalizedRetryAfter = Number(
        retryAfterHeader ?? rateLimitBody?.retry_after,
      );
      const method = (originalRequest.method || 'get').toLowerCase();
      const isIdempotent = ['get', 'head', 'options'].includes(method);
      if (
        isIdempotent
        && !originalRequest.skipRateLimitRetry
        && !originalRequest._rateLimitRetry
        && Number.isFinite(normalizedRetryAfter)
        && normalizedRetryAfter > 0
        && normalizedRetryAfter <= MAX_RATE_LIMIT_RETRY_AFTER_SECONDS
      ) {
        originalRequest._rateLimitRetry = true;
        await new Promise((resolve) => {
          setTimeout(resolve, normalizedRetryAfter * 1000);
        });
        return apiClient.request(originalRequest);
      }
      return Promise.reject(normalizeApiError(error, {
        code: 'RATE_LIMIT_EXCEEDED',
        details: {
          retry_after: Number.isFinite(normalizedRetryAfter)
            ? normalizedRetryAfter
            : undefined,
        },
      }));
    }

    // Handle 401 errors - attempt token refresh (only once)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't try to refresh if already on login page or auth endpoints
      const publicPaths = ['/login', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicPaths.some(path => currentPath.startsWith(path));
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                            originalRequest.url?.includes('/auth/refresh');

      // On a public route or an auth endpoint there is nothing to refresh:
      // fall through to the shared rejection below.
      if (!isPublicRoute && !isAuthEndpoint) {
        const sessionId = sessionStorage.getItem('auth_session_id');
        if (originalRequest._authSessionId !== undefined && originalRequest._authSessionId !== sessionId) {
          return Promise.reject(normalizeApiError(error));
        }
        const currentAccess = sessionStorage.getItem('access_token');
        const sentBearer = originalRequest.headers?.Authorization;
        if (sessionId && currentAccess && sentBearer && sentBearer !== `Bearer ${currentAccess}`) {
          // A late 401 for the old access token must use the rotation that
          // already completed, not consume the successor a second time.
          return apiClient(originalRequest);
        }
        const refreshToken = sessionStorage.getItem('refresh_token');
        try {
          // Attempt to refresh token using refresh_token from sessionStorage
          if (!refreshToken) {
            // Internal control flow, caught below — carries the original 401 so
            // no synthetic English message can escape to a caller.
            throw normalizeApiError(error);
          }

          await refreshSessionTokens();

          // Retry original request with new token
          return apiClient(originalRequest);
        } catch (refreshError) {
          if (sessionStorage.getItem('refresh_token') !== refreshToken) {
            return Promise.reject(refreshError);
          }
          // A timeout, network outage, 429 or 5xx says nothing about whether
          // the credential is valid. Preserve it and let the caller retry.
          if (!isAuthenticationRejection(refreshError)) {
            return Promise.reject(refreshError);
          }
          clearTokens();
          const handledByApp = dispatchSessionExpiredEvent();
          if (!handledByApp) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(normalizeApiError(error));
  }
);

// API methods
export const api = {
  get: <T>(url: string, config?: ApiRequestConfig) =>
    apiClient.get<T>(url, config).then(res => res.data),

  post: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then(res => res.data),

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then(res => res.data),

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then(res => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then(res => res.data),
};

// File upload with progress
// Note: Do NOT set Content-Type manually for FormData - axios will set it
// automatically with the correct boundary parameter
export const uploadFile = async (
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  timeoutMs: number = API_TIMEOUT,
  fields: Record<string, string> = {},
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));

  const config: AxiosRequestConfig = {
    timeout: timeoutMs,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  };

  return apiClient.post(url, formData, config).then(res => res.data);
};

export default apiClient;
